import { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import type { RockTCSReadingRow } from '../../../../types/rockTcs';
import { TCS_TABLE_COLUMNS } from '../rockTcsFields';
import { fmt } from '../sectionStyles';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';
import { FormulaHeaderTooltip } from '../../components/FormulaHeaderTooltip';

interface Props {
  readings: RockTCSReadingRow[];
  maxRowIndex: number | null;
  fiftyRowIndex: number | null;
}

/**
 * Combined Test Data & Calculations table (mockup: Result Entry bottom panel).
 * Grouped headers: Imported | Stress | Strains (µε) | Moduli & Ratios.
 * Paged at 50 rows; the failure row and the 50%-selection row are highlighted.
 */
export const CombinedDataTable = ({ readings, maxRowIndex, fiftyRowIndex }: Props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const { formulaFor } = useTestFormulas('rockTcs');

  const groups = useMemo(() => {
    const seen: { label: string; span: number }[] = [];
    for (const col of TCS_TABLE_COLUMNS) {
      const last = seen[seen.length - 1];
      if (last && last.label === col.group) last.span += 1;
      else seen.push({ label: col.group, span: 1 });
    }
    return seen;
  }, []);

  const pageRows = useMemo(
    () => readings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [readings, page, rowsPerPage],
  );

  return (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader sx={{ '& td, & th': { px: 0.75, py: 0.3, whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700 }} />
              {groups.map((g) => (
                <TableCell
                  key={g.label}
                  colSpan={g.span}
                  align="center"
                  sx={{ fontSize: '0.62rem', fontWeight: 700, borderLeft: '1px solid', borderLeftColor: 'divider' }}
                >
                  {g.label}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={{ top: 25, fontSize: '0.62rem', fontWeight: 700 }}>#</TableCell>
              {TCS_TABLE_COLUMNS.map((c) => {
                const reg = formulaFor(c.columnKey);
                return (
                  <FormulaHeaderTooltip
                    key={c.field}
                    label={`${c.label}${c.unit ? ` (${c.unit})` : ''}`}
                    formula={reg?.formula}
                    note={reg?.note}
                  >
                    <TableCell align="right" sx={{ top: 25, fontSize: '0.62rem', fontWeight: 700 }}>
                      {c.label}{c.unit ? ` (${c.unit})` : ''}
                    </TableCell>
                  </FormulaHeaderTooltip>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row, i) => {
              const idx = page * rowsPerPage + i;
              const isPeak = idx === maxRowIndex;
              const isFifty = idx === fiftyRowIndex;
              return (
                <TableRow
                  key={row.rockTCSReadingKey ?? idx}
                  sx={{
                    backgroundColor: isPeak ? 'rgba(198, 40, 40, 0.08)' : isFifty ? 'rgba(74, 103, 65, 0.10)' : undefined,
                  }}
                >
                  <TableCell sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                    {idx + 1}{isPeak ? ' ◆' : isFifty ? ' ●' : ''}
                  </TableCell>
                  {TCS_TABLE_COLUMNS.map((c) => (
                    <TableCell key={c.field} align="right" sx={{ fontSize: '0.68rem' }}>
                      {fmt(row[c.field as keyof RockTCSReadingRow] as number | null, c.decimals)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          ◆ failure row (max load) · ● 50%-of-TCS selection row
        </Typography>
        <TablePagination
          component="div"
          count={readings.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[50, 100, 250]}
        />
      </Box>
    </Paper>
  );
};
